import { getBusinessPartner, putBusinessPartner } from '../../api/businessPartner';
import {
  getBusinessParnerDropdownValues,
  getDutyAdminDropdown,
  getBusinessParnerDocumentsList,
  getBusinessParnerDocumentDetails,
  getBusinessParnerDocumentDescriptionDropdown,
  getBusinessParnerDocumentTypeDropdown,
  postBusinessPartnerDocument,
  deleteBusinessPartnerDocument,
  putBusinessPartnerRestoreDocument,
  getBusinessParnerAvailableDocumentsList,
  getNBPNotes,
  putBusinessPartnerParents,
  deleteBusinessPartnerContact,
} from '..//apiCalls';

/*this function will be responsible to fetch the data for the business partner, so we can populate the fields in the UI
params:
- title: string - the table title that we want to fetch the data for
- businessPartnerId: number - the id of the business partner we want to fetch the data for
- isTest: boolean - flag to indicate if we are in test mode or not. The default value is false
returns:
- data: object - the response from the API call, containing the data for the selected business partner
======================================================*/
export const fetchBusinessPartner = async (title, businessPartnerId, isTest = false, id = null) => {
  //check if title is BPTraderNotes. If that's the case, we want to return a specific object and not make a call
  if (title === 'Trader Notes' && id != null) {
    //calling the bpnotes API endpoint, get the length, as use inside the value
    const data = await getNBPNotes(id);

    const returnData = [{ subtitle: 'Notes', value: data?.length, Type: 'notes', data: data }];
    return returnData;
  }

  //TODO: change to fetchBusinessPartner
  let data = await getBusinessPartner(title, businessPartnerId, isTest);
  //check if title is 'Authorisation Documents' and if it is, add a new object to the data array to handle the documents list
  if (title === 'Authorisation Documents') {
    data.push({ subtitle: 'Documents', value: '', Type: 'documentsList' });
  }
  // Billable Settings: Duty Threshold, Duty Admin Fee, Duty Admin Dropdown, EORI TAN Usage
  // Merge Duty Admin Fee + Duty Admin Dropdown into one feeWithUnit row (same line)
  if (title === 'Billable Settings') {
    data = Array.isArray(data) ? [...data] : [];
    const feeItem = data.find((i) => i.subtitle === 'Duty Admin Fee');
    const dropdownItem = data.find((i) => i.subtitle === 'Duty Admin Dropdown');
    data = data
      .filter((i) => i.subtitle !== 'Duty Admin Fee' && i.subtitle !== 'Duty Admin Dropdown')
      .map((item) => ({
        ...item,
        Type: item.subtitle === 'Duty Threshold' ? 'integer' : item.Type,
      }));
    const mergedFee = {
      subtitle: 'Duty Admin Fee',
      value: feeItem?.value ?? null,
      unit: dropdownItem?.value ?? null,
      Type: 'feeWithUnit',
    };
    const thresholdIdx = data.findIndex((i) => i.subtitle === 'Duty Threshold');
    data.splice(thresholdIdx + 1, 0, mergedFee);
  }
  // Contacts: ensure exactly 5 rows (backend may return fewer or nothing for new BP);
  if (title === 'Contacts') {
    const arr = Array.isArray(data) ? data : [];
    const normalized = arr.slice(0, 5).map((row) => {
      const rowId = row?.id ?? row?.Id ?? row?.contactId ?? row?.ContactId ?? null;
      return {
        id: rowId != null && rowId !== '' ? rowId : null,
        contactName: row?.contactName ?? row?.ContactName ?? '',
        contactEmail: row?.contactEmail ?? row?.ContactEmail ?? '',
      };
    });
    while (normalized.length < 5) {
      normalized.push({ id: null, contactName: '', contactEmail: '' });
    }
    data = normalized;
  }

  if (title === 'Defaults') {
    const fieldsThatRelyOnMovementType = [
      'Country of Despatch',
      'Country of Destination',
      'Port Model',
      'Exit Port',
      'Entry Port',
      'Shipping Status',
    ];

    const updatedDefaults = data.map((currentDefault) => {
      if (fieldsThatRelyOnMovementType.includes(currentDefault.subtitle)) {
        return { ...currentDefault, requiresValueFromFieldId: 1 };
      }
      return currentDefault;
    });

    data = updatedDefaults;
  }
  return data;
};

/**
 * Fetch Duty Admin dropdown (£ and %) - uses dutyAdminDropdown API with id and charged params
 */
export const fetchDutyAdminDropdownValues = async (businessPartnerId, charged = 0) => {
  const response = await getDutyAdminDropdown(businessPartnerId, charged);
  return response;
};

/*this function will be responsible to fetch the dropdown values, so we can populate the dropdowns in the UI
params: 
- dropdown: string - the name of the dropdown we want to fetch the values for
returns:
- response: object - the response from the API call, containing the values for the selected dropdown
======================================================*/
export const fetchBusinessPartnerDropdownValues = async (dropdown) => {
  const dropdownsToFetch = [
    'CABIE Status',
    'Secondary Status',
    'Standing Authority',
    'FFD MoP',
    'Valuation Method',
    'Representation Type',
    'Representative',
    'Restrict VAT Options',
    'Country',
    'Billable Type',
    'Duty Admin Fee',
    'SLA',
    'Type',
  ];
  if (!dropdownsToFetch.includes(dropdown)) {
    return new Promise((resolve) => {
      resolve([
        {
          id: 1,
          data: 'No',
          statusNumber: '0',
        },
        {
          id: 2,
          data: 'Yes',
          statusNumber: '1',
        },
      ]);
    });
  } else {
    const response = await getBusinessParnerDropdownValues(dropdown);
    return response;
  }
};

/*this function will be responsible to save the business partner data
params:
- data: object - the data to be saved
- activeStep: number - the active step in the stepper
- businessPartnerId: number - the id of the business partner we want to save the data for
returns:
- response: object - the response from the API call, containing the company id and the possible validation errors for the data 
======================================================*/
export const saveBusinessPartner = async (data, activeStep, businessPartnerId = 1) => {
  const response = await putBusinessPartner(data, activeStep, businessPartnerId);
  return response;
};

export const fetchBusinessPartnerDocuments = async (businessPartnerId) => {
  const response = await getBusinessParnerDocumentsList(businessPartnerId);
  const filteredResponse = response.map(
    ({ id, Type, Description, Date, 'Added By': AddedBy, button, deleteRestore }) => ({
      id,
      Type,
      Description,
      Date,
      'Added By': AddedBy,
      button,
      deleteRestore,
    })
  );
  return filteredResponse;
};

export const fetchDeleteBPDocument = async (id) => {
  const response = await deleteBusinessPartnerDocument(id);
  return response;
};

export const fetchRestoreBPDocument = async (id) => {
  const response = await putBusinessPartnerRestoreDocument(id);
  return response;
};

export const fetchBusinessPartnerDocumentDetails = async (documentId) => {
  const response = await getBusinessParnerDocumentDetails(documentId);
  //on the response[0], add a new key called 'data', which is going to get the value of the column 'envelope' if 'envelope' is not null, otherwise, it will get the value of the column 'form'
  response[0].data = response[0].envelope ? response[0].envelope : response[0].form;
  return response;
};

export const fetchBusinessPartnerAvailableDocuments = async (businessPartnerId) => {
  const response = await getBusinessParnerAvailableDocumentsList(businessPartnerId);
  const filteredResponse = response.map(({ id, Type, Description, Date, 'Added By': AddedBy, button }) => ({
    id,
    Type,
    Description,
    Date,
    'Added By': AddedBy,
    button,
  }));
  return filteredResponse;
};

export const fetchBusinessPartnerDocumentDescriptionValues = async () => {
  const response = await getBusinessParnerDocumentDescriptionDropdown();
  return response;
};

export const fetchBusinessPartnerDocumentTypeValues = async () => {
  const response = await getBusinessParnerDocumentTypeDropdown();
  return response;
};

export const uploadBusinessPartnerFile = async (data) => {
  const response = await postBusinessPartnerDocument(data);
  return response;
};

export const saveBusinessPartnerParents = async (parentsData, businessPartnerId) => {
  // Extract ParentTraderId from all checked parents
  const parentIds = parentsData.filter((parent) => parent.checked).map((parent) => parent.ParentTraderId);

  const response = await putBusinessPartnerParents(parentIds, businessPartnerId);
  return response;
};

/**
 * Delete a single business partner contact by contact id.
 * Backend: DELETE /businesspartner/deleteContact?id={contactId}&neenorId={neenorId}
 */
export const deleteBPContact = async (contactId, neenorId) => {
  return deleteBusinessPartnerContact(contactId, neenorId);
};
